import type { SchemaChange } from '@tigergraph/tools-models/topology';
import type { SchemaChangeType } from '@/utils/analytics/types';

/**
 * The persisted-schema shape of EVT-017: the post-save type totals and whether this save
 * created the graph's schema or changed one.
 *
 * Two surfaces commit a schema — the designer's own Save button (`GraphResult.tsx`) and the
 * Load Data / Build AI confirm step (`Confirm.tsx`) — and both derive it the same way. The
 * totals are read off the designer, which already holds the post-save graph, rather than
 * counted from the change jobs, which are a delta.
 */

// Structural rather than the designer service itself: this is arithmetic over two lists, and
// typing it this way keeps it callable from a test without standing up the whole service.
export type SchemaTypeSource = {
  getAllVertexTypes?: () => unknown[] | undefined | null;
  getAllEdgeTypes?: () => unknown[] | undefined | null;
};

export type SchemaSavedShape = {
  schema_change_type: SchemaChangeType;
  vertex_type_count: number;
  edge_type_count: number;
};

export function buildSchemaSavedShape(
  designerService: SchemaTypeSource,
  schemaChangeJobs: SchemaChange[]
): SchemaSavedShape {
  const vertexTypeCount = (designerService.getAllVertexTypes?.() ?? []).length;
  const edgeTypeCount = (designerService.getAllEdgeTypes?.() ?? []).length;

  // Subtracting what these jobs added recovers the type count the graph had beforehand —
  // zero means this save created the schema rather than changing one.
  const addedTypes = schemaChangeJobs.reduce(
    (total, job) => total + (job.addVertexTypes?.length ?? 0) + (job.addEdgeTypes?.length ?? 0),
    0
  );
  const typesBeforeSave = vertexTypeCount + edgeTypeCount - addedTypes;

  return {
    schema_change_type: typesBeforeSave <= 0 ? 'initial_creation' : 'update',
    vertex_type_count: vertexTypeCount,
    edge_type_count: edgeTypeCount,
  };
}
